import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Loader2, Mail, PackagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { http, errorMessage, formatGBP } from "@/lib/api";

const STATUS = { available: "text-[#86efac]", used: "text-[#A1A1AA]", disabled: "text-red-300" };
const EMPTY = { name: "", contents: "", price: "", code: "", customer_label: "", notes: "" };

const CreateForm = ({ auth, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const pence = Math.round(parseFloat(f.price) * 100);
    const contents = f.contents.split("\n").map((x) => x.trim()).filter(Boolean);
    if (!f.name.trim() || !contents.length || !(pence >= 100)) return toast.error("Add a name, what's included and a monthly price.");
    setBusy(true);
    try {
      const { data } = await http.post("/admin/custom-packages", { name: f.name, contents, price_pence: pence, code: f.code, customer_label: f.customer_label, notes: f.notes }, auth());
      toast.success(`Code ${data.code} created.`);
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="custom-package-form">
      <div className="space-y-1.5"><Label htmlFor="cp-name">Package name</Label><Input id="cp-name" value={f.name} onChange={set("name")} maxLength={80} placeholder="e.g. 10 Net Bags" className="lc-input" data-testid="cp-name-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="cp-price">Monthly price (£)</Label><Input id="cp-price" type="number" min="1" step="0.01" value={f.price} onChange={set("price")} placeholder="60.00" className="lc-input" data-testid="cp-price-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="cp-contents">What's included (one item per line)</Label><Textarea id="cp-contents" rows={3} value={f.contents} onChange={set("contents")} placeholder={"10 net bags of kiln-dried logs\nLocal delivery included"} className="lc-input" data-testid="cp-contents-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="cp-customer">Who it's for (only you see this)</Label><Input id="cp-customer" value={f.customer_label} onChange={set("customer_label")} maxLength={120} placeholder="e.g. John, Ballyclare" className="lc-input" data-testid="cp-customer-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="cp-code">Code (leave blank to create one)</Label><Input id="cp-code" value={f.code} onChange={set("code")} maxLength={20} placeholder="e.g. JOHN10" className="lc-input uppercase" data-testid="cp-code-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="cp-notes">Private notes (only you see these)</Label><Textarea id="cp-notes" rows={2} value={f.notes} onChange={set("notes")} maxLength={1000} placeholder="e.g. Leave logs in the shed round the back" className="lc-input" data-testid="cp-notes-input" /></div>
      <button type="submit" disabled={busy} className="lc-btn-primary !py-3 sm:col-span-2" data-testid="cp-create">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PackagePlus className="h-4 w-4" /> Create package & code</>}
      </button>
    </form>
  );
};

const SendForm = ({ p, auth, onSent, onClose }) => {
  const [f, setF] = useState({ name: p.customer_label?.split(",")[0] || "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/custom-packages/${p.code}/send`, f, auth());
      toast.success(data.message);
      onSent();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-3 grid w-full gap-2 sm:grid-cols-[1fr_1.4fr_auto_auto]" data-testid={`cp-send-form-${p.code}`}>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} placeholder="First name" className="lc-input" data-testid={`cp-send-name-${p.code}`} />
      <Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Customer email" className="lc-input" data-testid={`cp-send-email-${p.code}`} />
      <button type="submit" disabled={busy} className="lc-btn-primary !py-2" data-testid={`cp-send-submit-${p.code}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send email"}</button>
      <button type="button" onClick={onClose} className="rounded-full px-3 py-2 text-sm text-[#A1A1AA] hover:text-white" data-testid={`cp-send-cancel-${p.code}`}>Cancel</button>
    </form>
  );
};

const NotesEditor = ({ p, auth, onChange }) => {
  const [text, setText] = useState(p.notes || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/custom-packages/${p.code}/notes`, { notes: text }, auth());
      toast.success("Notes saved.");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <div className="mt-3 w-full text-sm" data-testid={`cp-notes-${p.code}`}>
        {p.notes ? <p className="whitespace-pre-line rounded-xl border border-[#D4A437]/30 bg-[#D4A437]/5 px-3 py-2 text-[#F3E0A2]">{p.notes}</p> : null}
        <button onClick={() => setEditing(true)} className="mt-1 text-xs text-[#D4A437] hover:underline" data-testid={`cp-notes-edit-${p.code}`}>{p.notes ? "Edit notes" : "+ Add private notes"}</button>
      </div>
    );
  }
  return (
    <div className="mt-3 w-full space-y-2">
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="e.g. Leave logs in the shed round the back" className="lc-input" data-testid={`cp-notes-text-${p.code}`} />
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="lc-btn-primary !py-2" data-testid={`cp-notes-save-${p.code}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save notes"}</button>
        <button onClick={() => { setText(p.notes || ""); setEditing(false); }} className="rounded-full px-3 py-2 text-sm text-[#A1A1AA] hover:text-white" data-testid={`cp-notes-cancel-${p.code}`}>Cancel</button>
      </div>
    </div>
  );
};

const PackageRow = ({ p, auth, onChange }) => {
  const [sending, setSending] = useState(false);
  const copy = () => navigator.clipboard?.writeText(p.code).then(() => toast.success("Code copied."));
  const link = `${window.location.origin}/checkout?code=${encodeURIComponent(p.code)}`;
  const copyLink = () => navigator.clipboard?.writeText(link).then(() => toast.success("Sign-up link copied."));
  const disable = async () => {
    try {
      await http.post(`/admin/custom-packages/${p.code}/disable`, {}, auth());
      toast.success("Code disabled.");
      onChange();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#262626] p-4" data-testid={`cp-row-${p.code}`}>
      <div className="min-w-0">
        <p className="font-semibold">{p.name} · {formatGBP(p.price_pence)}/month {p.customer_label && <span className="text-sm text-[#A1A1AA]">({p.customer_label})</span>}</p>
        <p className="text-sm text-[#A1A1AA]">{p.contents.join(" · ")}</p>
        <p className={`text-xs ${STATUS[p.status] || ""}`} data-testid={`cp-status-${p.code}`}>
          {p.status === "used" ? `Used by ${p.used_by_email}` : p.status === "disabled" ? "Disabled" : "Available – not used yet"}
          {p.status === "available" && p.sent_to && <span className="text-[#A1A1AA]" data-testid={`cp-sent-${p.code}`}> · Emailed to {p.sent_to}{p.reminded_at && " · Reminder sent"}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-[#D4A437]/60 px-3 py-1.5 font-mono text-sm tracking-widest text-[#F3E0A2]" data-testid={`cp-copy-${p.code}`}><Copy className="h-3.5 w-3.5" />{p.code}</button>
        {p.status === "available" && !sending && <button onClick={() => setSending(true)} className="inline-flex items-center gap-1.5 rounded-full border border-[#FF6A00]/60 px-3 py-1.5 text-sm text-[#FF6A00] hover:bg-[#FF6A00]/10" data-testid={`cp-send-${p.code}`}><Mail className="h-3.5 w-3.5" />Send code</button>}
        {p.status === "available" && <button onClick={disable} className="rounded-full px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10" data-testid={`cp-disable-${p.code}`}>Disable</button>}
      </div>
      {p.status === "available" && (
        <div className="mt-3 flex w-full min-w-0 items-center gap-2 rounded-xl border border-[#262626] bg-black/40 px-3 py-2" data-testid={`cp-link-${p.code}`}>
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-[#D8D6D0]" data-testid={`cp-link-text-${p.code}`}>{link}</span>
          <button onClick={copyLink} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#FF6A00] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#ff7d1f]" data-testid={`cp-copy-link-${p.code}`}><Link2 className="h-3.5 w-3.5" />Copy link</button>
        </div>
      )}
      <NotesEditor p={p} auth={auth} onChange={onChange} />
      {sending && <SendForm p={p} auth={auth} onClose={() => setSending(false)} onSent={() => { setSending(false); onChange(); }} />}
    </li>
  );
};

export const CustomPackages = ({ auth }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    http.get("/admin/custom-packages", auth()).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [auth]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <section className="mt-8 rounded-3xl border border-[#262626] bg-[#101010] p-5 sm:p-6" data-testid="admin-custom-packages">
      <h2 className="font-display text-2xl font-bold">Custom packages</h2>
      <p className="mt-1 text-sm text-[#A1A1AA]">Create a package and give the customer the code. They enter it on the Plans page to subscribe. Each code works once.</p>
<p className="mt-1 text-xs text-[#8a8a8a]">Tip: you can add smokeless coal (20kg bags, £12 each) to custom packages; include it in "What's included".</p>
      <div className="mt-5"><CreateForm auth={auth} onCreated={load} /></div>
      {items.length > 0 && <ul className="mt-6 space-y-3">{items.map((p) => <PackageRow key={p.code} p={p} auth={auth} onChange={load} />)}</ul>}
    </section>
  );
};
